'use client';

import { motion } from 'framer-motion';
import { Globe, Layers, Briefcase } from 'lucide-react';
import { cn } from '@/lib/cn';

interface Segment {
  label: string;
  pct: number;
  color: string;
}

function DonutViz({ segments, center }: { segments: Segment[]; center: string }) {
  const offsets = segments.reduce<number[]>((acc, seg, i) => {
    acc.push(i === 0 ? 0 : acc[i - 1] + segments[i - 1].pct);
    return acc;
  }, []);
  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 36 36" className="w-24 h-24 flex-shrink-0" aria-hidden>
        {segments.map((seg, i) => (
          <circle
            key={seg.label}
            r="15.9155"
            cx="18"
            cy="18"
            fill="transparent"
            stroke={seg.color}
            strokeWidth="4"
            strokeDasharray={`${seg.pct} ${100 - seg.pct}`}
            strokeDashoffset={`${-offsets[i]}`}
            className="origin-center -rotate-90"
            style={{ transformOrigin: '18px 18px' }}
          />
        ))}
        <text
          x="18"
          y="19.5"
          textAnchor="middle"
          fontSize="4"
          fontWeight="600"
          fill="var(--color-text)"
        >
          {center}
        </text>
      </svg>
      <div className="space-y-1.5 min-w-0">
        {segments.map((seg) => (
          <div key={seg.label} className="flex items-center gap-2 text-xs">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
            <span className="truncate" style={{ color: 'var(--color-text-secondary)' }}>{seg.label}</span>
            <span className="font-medium text-[var(--color-text)]">{seg.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface CardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
  delay?: number;
}

function BasicsCard({ icon, title, description, children, delay = 0 }: CardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ y: -4 }}
      className={cn(
        'rounded-2xl p-6 border border-[var(--color-border-subtle)] h-full flex flex-col'
      )}
      style={{ backgroundColor: 'var(--color-surface)' }}
    >
      <div
        className="inline-flex items-center justify-center w-10 h-10 rounded-xl mb-4"
        style={{ backgroundColor: 'color-mix(in srgb, var(--color-accent) 15%, transparent)', color: 'var(--color-accent-muted)' }}
      >
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-[var(--color-text)]">{title}</h3>
      <p className="mt-1 mb-5 text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
        {description}
      </p>
      <div className="mt-auto">{children}</div>
    </motion.div>
  );
}

export function PortfolioBasics() {
  return (
    <section id="portfolio-basics" className="py-16 scroll-mt-24">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mb-10"
        >
          <span
            className="inline-block text-xs font-semibold uppercase tracking-wider mb-3"
            style={{ color: 'var(--color-accent-muted)' }}
          >
            1 · Portfolio basics
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-text)]">
            A balanced portfolio spreads risk three ways
          </h2>
          <p className="mt-3 text-lg" style={{ color: 'var(--color-text-secondary)' }}>
            Diversification is the closest thing to a free lunch in investing.
            Each dimension below reduces the damage any single event can do.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <BasicsCard
            icon={<Globe size={20} />}
            title="Geography"
            description="Markets in different countries don't move in lockstep. When one region is down, another is often up."
            delay={0.05}
          >
            <DonutViz
              center="Global"
              segments={[
                { label: 'United States', pct: 55, color: '#3b82f6' },
                { label: 'Canada', pct: 10, color: '#ef4444' },
                { label: 'Europe', pct: 20, color: '#a855f7' },
                { label: 'Emerging', pct: 15, color: '#f59e0b' },
              ]}
            />
          </BasicsCard>

          <BasicsCard
            icon={<Layers size={20} />}
            title="Asset type"
            description="Equities grow long-term but swing a lot. Bonds pay steady income and move less — they cushion portfolios when stocks fall."
            delay={0.15}
          >
            <DonutViz
              center="Mix"
              segments={[
                { label: 'Equities', pct: 70, color: '#10b981' },
                { label: 'Bonds (less volatile)', pct: 30, color: '#64748b' },
              ]}
            />
          </BasicsCard>

          <BasicsCard
            icon={<Briefcase size={20} />}
            title="Industry"
            description="Different industries rise and fall at different times. Spreading across sectors dampens the impact when one gets hit."
            delay={0.25}
          >
            <DonutViz
              center="Sectors"
              segments={[
                { label: 'Tech', pct: 30, color: '#3b82f6' },
                { label: 'Healthcare', pct: 20, color: '#22c55e' },
                { label: 'Financials', pct: 18, color: '#a855f7' },
                { label: 'Consumer', pct: 18, color: '#f59e0b' },
                { label: 'Energy', pct: 14, color: '#64748b' },
              ]}
            />
          </BasicsCard>
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.35 }}
          className="mt-8 text-center text-base sm:text-lg font-medium"
          style={{ color: 'var(--color-text-primary)' }}
        >
          A balanced portfolio spreads risk across all three.
        </motion.p>
      </div>
    </section>
  );
}
